<template>
  <div class="view-container">
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-project-diagram"></i> 流程名:</label>
          <input v-model="searchForm.processName" type="text" placeholder="请输入流程名" class="search-input" @keyup.enter="handleSearch">
        </div>
        <div class="search-item">
          <label><i class="fas fa-hashtag"></i> 任务号:</label>
          <input v-model="searchForm.taskNo" type="text" placeholder="请输入任务号" class="search-input" @keyup.enter="handleSearch">
        </div>
        <div class="search-item">
          <label><i class="fas fa-align-left"></i> 摘要信息:</label>
          <input v-model="searchForm.summary" type="text" placeholder="请输入摘要信息" class="search-input" style="width:180px" @keyup.enter="handleSearch">
        </div>
        <div class="search-item">
          <label><i class="fas fa-flag"></i> 优先级:</label>
          <select v-model="searchForm.priority" class="search-input" style="width:90px">
            <option value="">全部</option>
            <option value="高">高</option>
            <option value="中">中</option>
            <option value="低">低</option>
          </select>
        </div>
        <div class="search-actions">
          <button @click="handleSearch" class="btn btn-primary search-btn"><i class="fas fa-search"></i> 查询</button>
          <button @click="resetSearch" class="btn btn-default search-btn"><i class="fas fa-undo"></i> 重置</button>
        </div>
      </div>
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-list-ol"></i> 流水号:</label>
          <input v-model="searchForm.serialNo" type="text" placeholder="请输入流水号" class="search-input" @keyup.enter="handleSearch">
        </div>
        <div class="search-item">
          <label><i class="fas fa-user-plus"></i> 发起人:</label>
          <input v-model="searchForm.initiator" type="text" placeholder="请输入发起人" class="search-input" @keyup.enter="handleSearch">
        </div>
        <div class="search-item">
          <label><i class="fas fa-calendar-plus"></i> 发起时间:</label>
          <input v-model="searchForm.initiateTimeStart" type="date" class="search-input">
          <span class="date-separator">至</span>
          <input v-model="searchForm.initiateTimeEnd" type="date" class="search-input">
        </div>
        <div class="search-item">
          <label><i class="fas fa-info-circle"></i> 状态:</label>
          <select v-model="searchForm.status" class="search-input">
            <option value="">全部</option>
            <option value="0">处理中</option>
            <option value="1">已暂停</option>
            <option value="4">已审批</option>
            <option value="10003">已拒绝</option>
            <option value="10018">询问中</option>
            <option value="11111">加签中</option>
            <option value="6">已删除</option>
          </select>
        </div>
      </div>
    </div>

    <div class="table-wrapper">
      <div class="table-header-row">
        <div class="table-header-left"></div>
        <button @click="toggleColumnSelector" class="btn btn-default btn-sm column-settings-btn"><i class="fas fa-columns"></i> 列设置</button>
      </div>
      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th v-for="column in visibleColumns" :key="column.key"
                :class="{ 'sortable': column.sortable, 'sorted-asc': sortColumn === column.key && sortOrder === 'asc', 'sorted-desc': sortColumn === column.key && sortOrder === 'desc' }"
                @click="handleSort(column.key)" class="column-header"
                :style="column.width ? { width: column.width, minWidth: column.width } : {}">
                <span>{{ column.label }}</span>
                <i v-if="column.sortable" class="fas" :class="sortColumn === column.key ? (sortOrder === 'asc' ? 'fa-chevron-up' : 'fa-chevron-down') : 'fa-sort'"></i>
              </th>
              <th class="action-column">操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="task in paginatedTasks" :key="task.id" @dblclick="viewTask(task)" class="task-row">
              <td v-for="column in visibleColumns" :key="column.key">
                <template v-if="column.key === 'status'">
                  <span class="status-badge" :class="getStatusClass(task.statusCode)">{{ task.status }}</span>
                </template>
                <template v-else-if="column.key === 'priority'">
                  <span class="priority-badge" :class="task.priority === '高' ? 'high' : task.priority === '中' ? 'medium' : 'low'">{{ task.priority }}</span>
                </template>
                <template v-else-if="column.key === 'summary'">
                  <span class="summary-cell">{{ task.summary }}</span>
                </template>
                <template v-else-if="column.key === 'approvalDuration'">
                  <span>{{ task.approvalDuration }}</span>
                </template>
                <template v-else>
                  {{ task[column.key] }}
                </template>
              </td>
              <td class="action-column">
                <div class="action-buttons">
                  <button @click="viewTask(task)" class="btn-action view" title="查看"><i class="fas fa-eye"></i></button>
                  <!-- 撤回：仅状态为处理中(0)时可用 -->
                  <button v-if="task.statusCode === 0" @click="doAction(task, 'recall')" class="btn-action recall" title="撤回"><i class="fas fa-undo"></i></button>
                  <button @click="doAction(task, 'return')" class="btn-action return" title="退回重填"><i class="fas fa-reply"></i></button>
                  <button @click="doAction(task, 'transfer')" class="btn-action transfer" title="转办"><i class="fas fa-share"></i></button>
                  <!-- 指派：已审批(4)和已删除(6)时不可用 -->
                  <button v-if="task.statusCode !== 4 && task.statusCode !== 6" @click="doAction(task, 'delegate')" class="btn-action delegate" title="指派"><i class="fas fa-user-friends"></i></button>
                  <!-- 暂停/恢复：暂停(1)时显示恢复，其他状态显示暂停 -->
                  <button v-if="task.statusCode === 1" @click="doAction(task, 'resume')" class="btn-action resume" title="恢复"><i class="fas fa-play"></i></button>
                  <button v-else-if="task.statusCode !== 4 && task.statusCode !== 6" @click="doAction(task, 'pause')" class="btn-action pause" title="暂停"><i class="fas fa-pause"></i></button>
                  <button @click="doAction(task, 'specifyEnd')" class="btn-action end" title="指定结束"><i class="fas fa-stop-circle"></i></button>
                  <!-- 删除：已删除(6)时隐藏 -->
                  <button v-if="task.statusCode !== 6" @click="doAction(task, 'delete')" class="btn-action delete" title="删除"><i class="fas fa-trash"></i></button>
                </div>
              </td>
            </tr>
            <tr v-if="paginatedTasks.length === 0"><td :colspan="visibleColumns.length + 1" class="empty-cell">暂无任务数据</td></tr>
          </tbody>
        </table>
      </div>

      <div class="pagination-row">
        <div class="pagination-info">共 {{ totalTasks }} 条记录</div>
        <div class="pagination-controls">
          <div class="page-size-selector"><span>每页</span><select v-model="pageSize" class="page-size-input form-control" @change="handlePageSizeChange"><option :value="20">20</option><option :value="50">50</option><option :value="75">75</option><option :value="100">100</option></select><span>条</span></div>
          <button @click="prevPage" :disabled="currentPage===1" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-left"></i></button>
          <div class="page-numbers"><button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="['page-btn',{active:currentPage===page,disabled:page==='...'}]" :disabled="page==='...'">{{ page }}</button></div>
          <button @click="nextPage" :disabled="currentPage>=totalPages" class="btn btn-default btn-sm pagination-btn"><i class="fas fa-chevron-right"></i></button>
          <div class="page-jump"><span>跳转到</span><input type="number" v-model="jumpPage" min="1" :max="totalPages" class="jump-input" @keyup.enter="handleJump"><span>页</span><button @click="handleJump" class="btn btn-primary btn-sm jump-btn">GO</button></div>
        </div>
      </div>
    </div>

    <div v-if="showColumnSelector" class="modal-overlay" @click.self="toggleColumnSelector">
      <div class="modal-container modal-sm">
        <div class="modal-header"><h4>列设置</h4><button @click="toggleColumnSelector" class="close-btn"><i class="fas fa-times"></i></button></div>
        <div class="modal-body"><label v-for="column in allColumns" :key="column.key" class="column-checkbox"><input type="checkbox" v-model="column.visible" @change="updateVisibleColumns"><span>{{ column.label }}</span></label></div>
        <div class="modal-footer"><button @click="resetColumns" class="btn btn-default">重置</button><button @click="toggleColumnSelector" class="btn btn-primary">确定</button></div>
      </div>
    </div>

    <!-- 理由弹窗（退回重填/指定结束） -->
    <div class="modal-overlay" v-if="memoDialog.show" @click.self="memoDialog.show = false">
      <div class="modal-box">
        <div class="modal-header">{{ memoDialog.title }}</div>
        <div class="modal-body">
          <textarea v-model="memoDialog.memo" rows="4" placeholder="请输入理由..." class="form-textarea"></textarea>
        </div>
        <div class="modal-footer">
          <button @click="memoDialog.show = false" class="btn btn-default">取消</button>
          <button @click="submitMemoAction" :disabled="memoDialog.submitting" class="btn btn-primary">{{ memoDialog.submitting ? '提交中...' : '确认' }}</button>
        </div>
      </div>
    </div>

    <!-- 指派/转办弹窗 -->
    <div class="modal-overlay" v-if="forwardDialog.show" @click.self="forwardDialog.show = false">
      <div class="modal-box">
        <div class="modal-header">{{ forwardDialog.isDelegate ? '指派人员' : '转办人员' }}</div>
        <div class="modal-body">
          <label class="form-label">目标人</label>
          <div class="picker-input-row">
            <input :value="forwardDialog.pickedName || forwardDialog.targetAccount" readonly placeholder="点击选择人员" class="form-input picker-input" @click="openUserPicker" />
            <button type="button" class="btn btn-default btn-sm" @click="clearForwardPick" title="清除"><i class="fas fa-times"></i></button>
          </div>
          <label class="form-label" style="margin-top: 10px;">备注</label>
          <input v-model="forwardDialog.memo" placeholder="可选备注" class="form-input" />
        </div>
        <div class="modal-footer">
          <button @click="forwardDialog.show = false" class="btn btn-default">取消</button>
          <button @click="submitForward" :disabled="forwardDialog.submitting || !forwardDialog.targetAccount" class="btn btn-primary">{{ forwardDialog.submitting ? '提交中...' : '确认' }}</button>
        </div>
      </div>
    </div>

    <!-- 调度弹窗 -->
    <div class="modal-overlay" v-if="dispatchDialog.show" @click.self="dispatchDialog.show = false">
      <div class="modal-box">
        <div class="modal-header">任务调度</div>
        <div class="modal-body">
          <label class="form-label">目标节点</label>
          <select v-model="dispatchDialog.nodeId" class="form-input" :disabled="dispatchDialog.loading">
            <option value="">-- 请选择目标节点 --</option>
            <option v-for="node in dispatchNodes" :key="node.id" :value="node.id">{{ node.name }} ({{ node.typeName }})</option>
          </select>
          <div v-if="dispatchDialog.loading" style="color:#909399;font-size:12px;margin-top:4px;">正在加载节点列表...</div>
          <label class="form-label" style="margin-top: 10px;">备注</label>
          <input v-model="dispatchDialog.memo" placeholder="可选备注" class="form-input" />
        </div>
        <div class="modal-footer">
          <button @click="dispatchDialog.show = false" class="btn btn-default">取消</button>
          <button @click="submitDispatch" :disabled="dispatchDialog.submitting || !dispatchDialog.nodeId" class="btn btn-primary">{{ dispatchDialog.submitting ? '提交中...' : '确认调度' }}</button>
        </div>
      </div>
    </div>

    <!-- HandlerPicker 选人弹窗 -->
    <HandlerPicker
      :visible="userPicker.show"
      mode="user"
      title="选择人员"
      @close="userPicker.show = false"
      @pick="onUserPicked"
    />
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted } from 'vue'
import { getManageTask, buildTaskManageRequest, submitTask, forwardTask, taskAdminAction } from '../api/task'
import { getFlowNodes } from '../api/process'
import HandlerPicker from '../components/HandlerPicker.vue'

const showColumnSelector = ref(false)
const currentPage = ref(1)
const pageSize = ref(20)
const sortColumn = ref('taskNo')
const sortOrder = ref('desc')
const jumpPage = ref(1)

const searchForm = reactive({
  processName: '', taskNo: '', summary: '', priority: '',
  serialNo: '', initiator: '', initiateTimeStart: '', initiateTimeEnd: '', status: ''
})

const allColumns = ref([
  { key: 'taskNo', label: '任务号', sortable: true, visible: true, width: '100px' },
  { key: 'serialNo', label: '流水号', sortable: true, visible: true, width: '160px' },
  { key: 'processName', label: '流程名', sortable: true, visible: true, width: '200px' },
  { key: 'initiator', label: '发起人', sortable: true, visible: true, width: '100px' },
  { key: 'initiateTime', label: '发起时间', sortable: true, visible: true, width: '180px' },
  { key: 'approvalDuration', label: '审批时长', sortable: true, visible: true, width: '100px' },
  { key: 'status', label: '状态', sortable: false, visible: true, width: '120px' },
  { key: 'summary', label: '摘要信息', sortable: false, visible: true, width: '250px' },
  { key: 'priority', label: '优先级', sortable: false, visible: true, width: '80px' }
])

const tasks = ref([])

const fmt = (v) => {
  if (v === undefined || v === null) return ''
  return String(v).replace('T', ' ').substring(0, 19)
}

const formatDuration = (val) => {
  // Vw_AllowViewTask 直接返回已格式化的字符串 (Spendtime 列)，如 "1天3小时45分6秒"
  // 也兼容可查看视图返回的数字小时
  if (val === undefined || val === null || val === '') return '-'
  const s = String(val).trim()
  if (!s) return '-'
  // 已是字符串形式（含中文"天/小时/分/秒"）
  if (/[\u4e00-\u9fa5]/.test(s)) return s
  const h = Number(s)
  if (isNaN(h)) return s
  if (h === 0) return '-'
  if (h < 1) return Math.round(h * 60) + ' 分钟'
  if (h < 24) return h.toFixed(1) + ' 小时'
  return (h / 24).toFixed(1) + ' 天'
}

const priorityName = (p) => {
  const s = (p ?? '').toString()
  if (['高', '中', '低'].includes(s)) return s
  const n = Number(p)
  return n === 2 ? '高' : n === 0 ? '低' : '中'
}

const getStatusClass = (code) => {
  const map = { 0: 'processing', 1: 'paused', 4: 'approved', 10003: 'rejected', 6: 'deleted' }
  return map[code] || 'default'
}

const mapTask = (t) => ({
  id: t.taskId ?? t.TaskId,
  taskNo: String(t.taskId ?? t.TaskId ?? ''),
  serialNo: t.serialNum || t.SerialNum || '',
  processName: t.processName || t.ProcessName || '',
  initiator: t.initiatorDisplayName || t.InitiatorDisplayName || t.initiator || t.Initiator || '',
  initiateTime: fmt(t.createAt || t.CreateAt),
  approvalDuration: formatDuration(t.spendtime || t.Spendtime || t.spendTime || t.SpendTime || 0),
  approvalDurationRaw: t.spendtime || t.Spendtime || '',
  status: t.statusName || t.StatusName || '',
  statusCode: t.status ?? t.Status ?? 0,
  summary: t.description || t.Description || t.para0 || t.Para0 || '',
  priority: priorityName(t.priority ?? t.Priority),
  showMore: false,
  _raw: t,
  formGuid: t.formGuid || t.FormGuid || '',
  formType: t.formType || t.FormType || t.designerType || t.DesignerType || '2',
  formData: t.formData || t.FormData || {},
  flowGuid: t.flowGuid || t.FlowGuid || t.processGuid || t.ProcessGuid || '',
  // 操作权限位（用于控制"退回重填/撤回/催办"等按钮是否可见）
  taskOperationPermission: t.taskOperationPermission || t.TaskOperationPermission || '',
})

const loadTasks = async () => {
  try {
    const req = buildTaskManageRequest({
      pageNumber: 0,
      pageSize: 500,
      sortName: '',
      sortOrder: 'desc',
      processName: searchForm.processName,
      taskNo: searchForm.taskNo,
      summary: searchForm.summary,
      priority: searchForm.priority,
      serialNo: searchForm.serialNo,
      initiator: searchForm.initiator,
      initiateTimeStart: searchForm.initiateTimeStart,
      initiateTimeEnd: searchForm.initiateTimeEnd,
      status: searchForm.status
    })
    const res = await getManageTask(req)
    console.log('[任务管理] API原始响应:', res)
    const rows = res.rows || res.Rows || []
    console.log('[任务管理] 行数据(条数):', rows.length, rows)
    tasks.value = rows.map(mapTask)
    console.log('[任务管理] 映射后数据:', tasks.value)
  } catch (e) {
    console.error('加载任务管理数据失败', e)
  }
}

onMounted(loadTasks)

const visibleColumns = computed(() => allColumns.value.filter(c => c.visible))

const filteredTasks = computed(() => {
  // 服务端已过滤，客户端不再重复过滤；仅做排序
  let result = [...tasks.value]
  const colDef = allColumns.value.find(c => c.key === sortColumn.value)
  if (colDef && colDef.sortable) {
    const key = sortColumn.value
    result.sort((a, b) => {
      const va = a[key] ?? '', vb = b[key] ?? ''
      let cmp = 0
      if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb
      else cmp = String(va).localeCompare(String(vb))
      return sortOrder.value === 'desc' ? -cmp : cmp
    })
  }
  return result
})

const totalTasks = computed(() => filteredTasks.value.length)
const totalPages = computed(() => Math.ceil(totalTasks.value / pageSize.value) || 1)
const paginatedTasks = computed(() => {
  const s = (currentPage.value - 1) * pageSize.value
  return filteredTasks.value.slice(s, s + pageSize.value)
})

const visiblePages = computed(() => {
  const pages = [], total = totalPages.value, current = currentPage.value
  if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i) }
  else { pages.push(1); if (current > 4) pages.push('...'); for (let i = Math.max(2, current - 2); i <= Math.min(total - 1, current + 2); i++) pages.push(i); if (current < total - 3) pages.push('...'); pages.push(total) }
  return pages
})

const handleSort = (col) => {
  if (!allColumns.value.find(c => c.key === col)?.sortable) return
  if (sortColumn.value === col) sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  else { sortColumn.value = col; sortOrder.value = 'asc' }
  currentPage.value = 1
}

const handleSearch = () => { currentPage.value = 1; loadTasks() }
const resetSearch = () => { Object.keys(searchForm).forEach(k => searchForm[k] = ''); currentPage.value = 1; loadTasks() }
const prevPage = () => { if (currentPage.value > 1) currentPage.value-- }
const nextPage = () => { if (currentPage.value < totalPages.value) currentPage.value++ }
const goToPage = (p) => { if (typeof p === 'number' && p >= 1 && p <= totalPages.value) currentPage.value = p }
const handleJump = () => { const p = parseInt(jumpPage.value); p >= 1 && p <= totalPages.value ? currentPage.value = p : jumpPage.value = currentPage.value }
const handlePageSizeChange = () => { currentPage.value = 1 }

const toggleColumnSelector = () => { showColumnSelector.value = !showColumnSelector.value }
const updateVisibleColumns = () => { if (allColumns.value.filter(c => c.visible).length < 1) { const h = allColumns.value.find(c => !c.visible); if (h) h.visible = true } }
const resetColumns = () => { allColumns.value.forEach(c => c.visible = true) }

// 对话框状态
const forwardDialog = reactive({ show: false, task: null, targetAccount: '', pickedName: '', isDelegate: false, memo: '', submitting: false })
const memoDialog = reactive({ show: false, task: null, action: '', title: '', memo: '', submitting: false })
const dispatchDialog = reactive({ show: false, task: null, nodeId: '', memo: '', submitting: false, loading: false })
const dispatchNodes = ref([])
const userPicker = reactive({ show: false })

const viewTask = (t) => {
  window.dispatchEvent(new CustomEvent('navigateToTaskView', {
    detail: {
      taskId: t.taskNo || t.id || t.TaskNo,
      mode: 'view',
      processName: t.processName || t.ProcessName || '',
      title: `任务-${t.processName || t.ProcessName || t.taskNo || t.id}`,
      taskData: t
    }
  }))
}

const doAction = async (t, action) => {
  const raw = t._raw || {}
  const stepId = raw.stepId || raw.StepId || 0
  const taskId = t.id

  // 查看
  if (action === 'view' || action === 'flowchart') { viewTask(t); return }

  // 撤回 — 调用 AdminAction withdraw（管理员操作）
  if (action === 'recall' || action === 'withdraw') {
    if (!await $confirm(`确定撤回 "${t.processName}" 吗？`)) return
    try {
      const res = await taskAdminAction({ taskId, stepId, action: 'withdraw' })
      const r = res.data || res.Data || res || {}
      $message.success(r.message || r.Message || '撤回成功')
      loadTasks()
    } catch (e) { $message.error('撤回失败: ' + (e.message || '未知错误')) }
    return
  }

  // 退回重填 — 弹出理由框
  if (action === 'return' || action === 'reject') {
    memoDialog.task = t; memoDialog.action = 'recedeRestart'
    memoDialog.title = '退回重填理由'; memoDialog.memo = ''; memoDialog.show = true
    return
  }

  // 转办 — 弹出选人框
  if (action === 'transfer' || action === 'forward') {
    forwardDialog.task = t; forwardDialog.targetAccount = ''; forwardDialog.pickedName = ''; forwardDialog.isDelegate = false; forwardDialog.memo = ''; forwardDialog.show = true
    return
  }

  // 委托/指派 — 弹出选人框
  if (action === 'delegate' || action === 'entrust') {
    forwardDialog.task = t; forwardDialog.targetAccount = ''; forwardDialog.pickedName = ''; forwardDialog.isDelegate = true; forwardDialog.memo = ''; forwardDialog.show = true
    return
  }

  // 删除
  if (action === 'delete') {
    if (!await $confirm(`确定删除 "${t.processName}" 吗？此操作不可恢复！`)) return
    try {
      const res = await taskAdminAction({ taskId, stepId, action: 'delete' })
      const r = res.data || res.Data || res || {}
      $message.success(r.message || r.Message || '删除成功')
      loadTasks()
    } catch (e) { $message.error('删除失败: ' + (e.message || '未知错误')) }
    return
  }

  // 暂停
  if (action === 'pause') {
    if (!await $confirm(`确定暂停 "${t.processName}" 吗？`)) return
    try {
      const res = await taskAdminAction({ taskId, stepId, action: 'pause' })
      const r = res.data || res.Data || res || {}
      $message.success(r.message || r.Message || '暂停成功')
      loadTasks()
    } catch (e) { $message.error('暂停失败: ' + (e.message || '未知错误')) }
    return
  }

  // 恢复
  if (action === 'resume') {
    if (!await $confirm(`确定恢复 "${t.processName}" 吗？`)) return
    try {
      const res = await taskAdminAction({ taskId, stepId, action: 'resume' })
      const r = res.data || res.Data || res || {}
      $message.success(r.message || r.Message || '恢复成功')
      loadTasks()
    } catch (e) { $message.error('恢复失败: ' + (e.message || '未知错误')) }
    return
  }

  // 指定结束 — 弹出理由框
  if (action === 'specifyEnd' || action === 'end') {
    memoDialog.task = t; memoDialog.action = 'end'
    memoDialog.title = '指定结束理由'; memoDialog.memo = ''; memoDialog.show = true
    return
  }

  // 催办
  if (action === 'urge') {
    if (!await $confirm(`确定催办 "${t.processName}" 的当前处理人吗？`)) return
    try {
      const res = await taskAdminAction({ taskId, stepId, action: 'urge', memo: '管理员催办' })
      const r = res.data || res.Data || res || {}
      $message.success(r.message || r.Message || '催办成功')
      loadTasks()
    } catch (e) { $message.error('催办失败: ' + (e.message || '未知错误')) }
    return
  }
  // 调度 — 弹出节点选择框
  if (action === 'dispatch' || action === 'jump') {
    dispatchDialog.task = t; dispatchDialog.nodeId = ''; dispatchDialog.memo = ''; dispatchDialog.show = true
    loadDispatchNodes(t._raw || t)
    return
  }
}

const submitMemoAction = async () => {
  const d = memoDialog
  if (!d.memo || !d.memo.trim()) { $message.warning('请填写理由'); return }
  d.submitting = true
  try {
    const raw = d.task._raw || {}
    const res = await taskAdminAction({
      taskId: d.task.id,
      stepId: raw.stepId || raw.StepId || 0,
      action: d.action,
      memo: d.memo
    })
    const r = res.data || res.Data || res || {}
    $message.success(r.message || r.Message || '操作成功')
    d.show = false
    loadTasks()
  } catch (e) { $message.error('操作失败: ' + (e.message || '未知错误')) }
  finally { d.submitting = false }
}

const submitForward = async () => {
  const d = forwardDialog
  if (!d.targetAccount || !d.targetAccount.trim()) { $message.warning('请填写目标人账号'); return }
  d.submitting = true
  try {
    const raw = d.task._raw || {}
    const res = await taskAdminAction({
      taskId: d.task.id,
      stepId: raw.stepId || raw.StepId || 0,
      action: 'entrust',
      targetAccount: d.targetAccount,
      memo: d.memo
    })
    const r = res.data || res.Data || res || {}
    $message.success(r.message || r.Message || '操作成功')
    d.show = false
    loadTasks()
  } catch (e) { $message.error('操作失败: ' + (e.message || '未知错误')) }
  finally { d.submitting = false }
}

// 调度：加载流程节点列表
const loadDispatchNodes = async (raw) => {
  const flowGuid = raw.processGuid || raw.ProcessGuid || raw.flowGuid || raw.FlowGuid || ''
  if (!flowGuid) { $message.error('无法获取流程信息'); dispatchDialog.show = false; return }
  dispatchDialog.loading = true
  dispatchNodes.value = []
  try {
    const res = await getFlowNodes(flowGuid)
    const list = res?.data ?? res?.Data ?? res ?? []
    const nodeTypeNames = { 0:'开始',1:'审批',2:'决策',3:'条件',4:'分流',5:'合流',6:'通知',7:'快照',8:'结束',35:'子流程',42:'SQL插件',43:'WebApi' }
    dispatchNodes.value = (Array.isArray(list) ? list : []).map(n => ({
      id: String(n.id ?? n.Id ?? ''),
      name: n.name ?? n.Name ?? '',
      typeName: nodeTypeNames[n.nodeType ?? n.NodeType] || '未知'
    }))
  } catch (e) { console.error('加载节点列表失败', e) }
  finally { dispatchDialog.loading = false }
}

// 调度：提交
const submitDispatch = async () => {
  const d = dispatchDialog
  if (!d.nodeId) { $message.warning('请选择目标节点'); return }
  if (!await $confirm('确定调度该任务到目标节点吗？')) return
  d.submitting = true
  try {
    const res = await taskAdminAction({
      taskId: d.task.id,
      stepId: (d.task._raw?.stepId || d.task._raw?.StepId || 0),
      action: 'jump',
      jumpNodeId: d.nodeId,
      memo: d.memo || '管理员调度'
    })
    const r = res.data || res.Data || res || {}
    $message.success(r.message || r.Message || '调度成功')
    d.show = false
    loadTasks()
  } catch (e) { $message.error('调度失败: ' + (e.message || '未知错误')) }
  finally { d.submitting = false }
}

// HandlerPicker: 打开选人弹窗
const openUserPicker = () => { userPicker.show = true }

// HandlerPicker: 清除已选人员
const clearForwardPick = () => {
  forwardDialog.targetAccount = ''
  forwardDialog.pickedName = ''
}

// HandlerPicker: 选人回调
const onUserPicked = (result) => {
  forwardDialog.targetAccount = result.account || result.name || ''
  forwardDialog.pickedName = result.name || ''
}
</script>

<style scoped>
.view-container { padding: 20px; background: #fff; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.05); display: flex; flex-direction: column; max-height: calc(100vh - 150px); overflow: hidden; }

.btn { padding: 6px 15px; border-radius: 4px; border: none; cursor: pointer; font-size: 13px; display: inline-flex; align-items: center; gap: 5px; transition: all 0.2s ease; }
.btn-primary { background-color: #1ab394; color: #fff; }
.btn-primary:hover { background-color: #18a689; }
.btn-default { background-color: #f3f3f4; color: #676a6c; border: 1px solid #e7eaec; }
.btn-default:hover { background-color: #e7eaec; }
.btn-info { background-color: #31b0d5; color: #fff; }
.btn-info:hover { background-color: #269abc; }
.btn-warning { background-color: #f0ad4e; color: #fff; }
.btn-warning:hover { background-color: #ec971f; }
.btn-danger { background-color: #d9534f; color: #fff; }
.btn-danger:hover { background-color: #c9302c; }
.btn-xs { padding: 3px 8px; font-size: 12px; margin-right: 3px; }
.btn-sm { padding: 5px 12px; font-size: 12px; }

.search-panel { background: #fff; border: 1px solid #e0e4e8; border-radius: 8px; padding: 20px; margin-bottom: 15px; flex-shrink: 0; box-shadow: 0 1px 4px rgba(0,0,0,0.04); }
.search-row { display: flex; flex-wrap: wrap; gap: 16px; align-items: flex-end; }
.search-row + .search-row { margin-top: 10px; }
.search-item { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
.search-item label { font-size: 13px; color: #6b7280; font-weight: 500; }
.search-item label i { margin-right: 2px; color: #1ab394; font-size: 12px; }
.search-input { padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; width: 150px; outline: none; transition: border-color 0.2s, box-shadow 0.2s; background: #fafbfc; }
.search-input:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); background: #fff; }
.search-input[type="date"] { width: 135px; }
.date-separator { color: #676a6c; }
.search-actions { display: flex; gap: 10px; margin-left: auto; align-self: flex-end; }
.search-btn { padding: 8px 18px; font-size: 13px; border-radius: 6px; font-weight: 500; }

.table-wrapper { border: 1px solid #e7eaec; border-radius: 4px; overflow: hidden; display: flex; flex-direction: column; flex: 1; min-height: 0; }
.table-header-row { display: flex; justify-content: space-between; align-items: center; padding: 10px 15px; background-color: #f9f9f9; border-bottom: 1px solid #e7eaec; flex-shrink: 0; }
.table-header-left { display: flex; gap: 8px; }
.table-container { overflow-y: auto; flex: 1; min-height: 0; }
.data-table { width: 100%; border-collapse: collapse; table-layout: fixed; }
.data-table th, .data-table td { padding: 10px 15px; text-align: left; border-bottom: 1px solid #e7eaec; white-space: nowrap; font-size: 13px; color: #676a6c; overflow: hidden; text-overflow: ellipsis; }
.data-table th { background-color: #f5f5f5; font-weight: 600; position: sticky; top: 0; z-index: 2; }
.data-table tbody tr:hover { background: #f9fafb; }.task-row { cursor: pointer; }
.data-table input[type="checkbox"] { width: 16px; height: 16px; cursor: pointer; }
.column-header { cursor: pointer; user-select: none; white-space: nowrap; }
.column-header:hover { background-color: #e7eaec; }
.column-header i { margin-left: 4px; color: #9ca3af; font-size: 11px; vertical-align: middle; }
.sorted-asc, .sorted-desc { background-color: #e8f5f3; }
.summary-cell { max-width: 180px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: inline-block; }

.status-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; font-weight: 500; }
.status-badge.processing { background: #dbeafe; color: #1d4ed8; }
.status-badge.paused { background: #fef9c3; color: #a16207; }
.status-badge.approved { background: #dcfce7; color: #16a34a; }
.status-badge.rejected { background: #fee2e2; color: #dc2626; }
.status-badge.deleted { background: #f3f4f6; color: #9ca3af; }
.status-badge.default { background: #e8f4fd; color: #2563eb; }

.priority-badge { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; font-weight: 500; }
.priority-badge.high { background: #fee2e2; color: #dc2626; }
.priority-badge.medium { background: #fef9c3; color: #a16207; }
.priority-badge.low { background: #e8f4fd; color: #2563eb; }

.empty-cell { text-align: center; padding: 40px; color: #999; }

.pagination-row { display: flex; justify-content: space-between; align-items: center; padding: 12px 15px; background-color: #f9f9f9; border-top: 1px solid #e7eaec; flex-shrink: 0; }
.pagination-info { font-size: 13px; color: #676a6c; }
.pagination-controls { display: flex; align-items: center; gap: 10px; }
.page-numbers { display: flex; gap: 5px; }
.page-btn { width: 32px; height: 32px; border: 1px solid #e7eaec; background-color: #fff; color: #676a6c; border-radius: 4px; cursor: pointer; font-size: 13px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
.page-btn:hover:not(.disabled) { background-color: #f9f9f9; border-color: #1ab394; color: #1ab394; }
.page-btn.active { background-color: #1ab394; border-color: #1ab394; color: #fff; }
.page-btn.disabled { cursor: not-allowed; opacity: 0.5; }
.pagination-btn { width: 32px; height: 32px; padding: 0; display: flex; align-items: center; justify-content: center; }
.page-size-selector { display: flex; align-items: center; gap: 5px; margin-right: 10px; padding-right: 10px; border-right: 1px solid #e7eaec; font-size: 13px; }
.page-size-input { width: 60px; padding: 5px 8px; font-size: 13px; }
.page-jump { display: flex; align-items: center; gap: 5px; margin-left: 10px; padding-left: 10px; border-left: 1px solid #e7eaec; }
.page-jump span { font-size: 13px; color: #676a6c; }
.jump-input { width: 50px; padding: 5px 8px; border: 1px solid #e7eaec; border-radius: 4px; font-size: 13px; text-align: center; }
.jump-input:focus { outline: none; border-color: #1ab394; box-shadow: 0 0 0 2px rgba(26,179,148,0.2); }
.jump-btn { padding: 5px 12px; }

.action-column { white-space: nowrap; width: 240px; }
.action-buttons { display: inline-flex; gap: 4px; align-items: center; }
.btn-action { width: 26px; height: 26px; border-radius: 4px; border: 1px solid transparent; background: #f5f7fa; color: #5a6a7a; cursor: pointer; font-size: 12px; display: inline-flex; align-items: center; justify-content: center; transition: all 0.18s ease; }
.btn-action:hover { background: #eef2f7; transform: translateY(-1px); box-shadow: 0 2px 4px rgba(0,0,0,0.08); }
.btn-action.view { color: #31b0d5; }
.btn-action.view:hover { background: #e6f6fb; }
.btn-action.recall { color: #f0ad4e; }
.btn-action.recall:hover { background: #fdf3e3; }
.btn-action.return { color: #ec5e5e; }
.btn-action.return:hover { background: #fdecec; }
.btn-action.transfer { color: #9b59b6; }
.btn-action.transfer:hover { background: #f3e8fa; }
.btn-action.delegate { color: #34495e; }
.btn-action.delegate:hover { background: #eaeef2; }
.btn-action.urge { color: #e74c3c; }
.btn-action.urge:hover { background: #fbeae9; }
.btn-action.flowchart { color: #1ab394; }
.btn-action.flowchart:hover { background: #e6f6f1; }
.btn-action.pause { color: #f39c12; }
.btn-action.pause:hover { background: #fef5e7; }
.btn-action.resume { color: #27ae60; }
.btn-action.resume:hover { background: #e8f8f0; }
.btn-action.end { color: #555; }
.btn-action.end:hover { background: #eaeaea; }
.btn-action.delete { color: #d9534f; }
.btn-action.delete:hover { background: #fbeaea; }

.column-checkbox { display: flex; align-items: center; gap: 8px; padding: 6px 0; cursor: pointer; font-size: 13px; color: #676a6c; }
.modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.45); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal-container { background: #fff; border-radius: 8px; box-shadow: 0 8px 30px rgba(0,0,0,0.18); display: flex; flex-direction: column; max-height: 85vh; width: 460px; }
.modal-container.modal-sm { width: 420px; }
.modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e7eaec; flex-shrink: 0; }
.modal-header h4 { margin: 0; font-size: 14px; color: #1a1a1a; }
.close-btn { background: none; border: none; font-size: 18px; color: #999; cursor: pointer; padding: 4px 8px; border-radius: 4px; }
.close-btn:hover { background: #f0f0f0; color: #333; }
.modal-body { padding: 20px; overflow-y: auto; }
.modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }

/* 弹窗表单 */
.modal-box { background: #fff; border-radius: 8px; box-shadow: 0 8px 30px rgba(0,0,0,0.18); width: 460px; max-height: 85vh; display: flex; flex-direction: column; }
.modal-box .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; border-bottom: 1px solid #e7eaec; font-size: 15px; font-weight: 600; color: #1a1a1a; }
.modal-box .modal-body { padding: 20px; overflow-y: auto; }
.modal-box .modal-footer { padding: 12px 20px; border-top: 1px solid #e7eaec; display: flex; justify-content: flex-end; gap: 10px; flex-shrink: 0; }
.form-label { display: block; font-size: 13px; color: #6b7280; margin-bottom: 4px; font-weight: 500; }
.form-input { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; outline: none; box-sizing: border-box; }
.form-input:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); }
.form-textarea { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 6px; font-size: 13px; outline: none; resize: vertical; box-sizing: border-box; }
.form-textarea:focus { border-color: #1ab394; box-shadow: 0 0 0 3px rgba(26,179,148,0.12); }
.picker-input-row { display: flex; gap: 6px; align-items: center; }
.picker-input { cursor: pointer; background: #fafbfc; }
.picker-input:hover { border-color: #1ab394; }
</style>
